import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { adminSponsorPricingApi } from '../../api/adminSponsorPricing.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import { formatTierLabel } from './sponsorPricingFormatters';
import '../subscription-plans/subscription-plans.css';
import './sponsor-pricing.css';

const emptyForm = {
  tierNumber: '',
  minAssociates: '',
  maxAssociates: '',
  pricePerAssociateAnnual: '',
  pricingModelLabel: 'Annual Single Fee',
  isActive: true,
};

export default function SponsorPricingEditor() {
  const { tierId } = useParams();
  const navigate = useNavigate();
  const isNew = tierId === 'new';
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      setLoading(true);
      const result = await apiHandler(() => adminSponsorPricingApi.getById(tierId));
      if (result.success) {
        const data = unwrapData(result);
        setForm({
          tierNumber: data.tierNumber ?? '',
          minAssociates: data.minAssociates ?? '',
          maxAssociates: data.maxAssociates ?? '',
          pricePerAssociateAnnual: data.pricePerAssociateAnnual ?? '',
          pricingModelLabel: data.pricingModelLabel || 'Annual Single Fee',
          isActive: data.isActive !== false,
        });
      } else {
        setError(result.message || 'Failed to load tier');
      }
      setLoading(false);
    };
    load();
  }, [isNew, tierId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    tierNumber: Number(form.tierNumber),
    minAssociates: Number(form.minAssociates),
    maxAssociates: Number(form.maxAssociates),
    pricePerAssociateAnnual: Number(form.pricePerAssociateAnnual),
    pricingModelLabel: form.pricingModelLabel.trim() || 'Annual Single Fee',
    isActive: !!form.isActive,
  });

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    const payload = buildPayload();
    const result = await apiHandler(() => (
      isNew
        ? adminSponsorPricingApi.create(payload)
        : adminSponsorPricingApi.update(tierId, payload)
    ));
    if (result.success) {
      const data = unwrapData(result);
      setMessage(result.message || (isNew ? 'Tier created' : 'Tier updated'));
      if (isNew && data?.id) {
        navigate(`/sponsor-pricing/${data.id}/edit`, { replace: true });
      }
    } else {
      setError(result.message || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="sp-page"><p className="sp-loading">Loading tier…</p></div>;
  }

  return (
    <div className="sp-page">
      <PageHeader
        title={isNew ? 'Add Sponsor Pricing Tier' : `Edit ${formatTierLabel(form.tierNumber)}`}
        subtitle="Annual per-associate pricing for enterprise sponsor groups"
        actions={(
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/sponsor-pricing')}>
            <i className="fa-solid fa-arrow-left" /> Back to list
          </button>
        )}
      />

      {error && <div className="sp-alert sp-alert-danger">{error}</div>}
      {message && <div className="sp-alert sp-alert-success">{message}</div>}

      <form onSubmit={handleSave}>
        <div className="sp-form-stack">
          <div className="sp-card">
            <div className="sp-card-header">
              <i className="fa-solid fa-tags" />
              <h4>Tier details</h4>
            </div>
            <div className="sp-form-grid-2">
              <div className="sp-form-row">
                <label>Tier number</label>
                <input
                  type="number"
                  min={1}
                  required
                  value={form.tierNumber}
                  onChange={(e) => updateField('tierNumber', e.target.value)}
                />
              </div>
              <div className="sp-form-row">
                <label>Min users</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  required
                  value={form.minAssociates}
                  onChange={(e) => updateField('minAssociates', e.target.value)}
                />
              </div>
              <div className="sp-form-row">
                <label>Max users</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  required
                  value={form.maxAssociates}
                  onChange={(e) => updateField('maxAssociates', e.target.value)}
                />
              </div>
              <div className="sp-form-row">
                <label>Annual price / user ($)</label>
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  required
                  value={form.pricePerAssociateAnnual}
                  onChange={(e) => updateField('pricePerAssociateAnnual', e.target.value)}
                />
              </div>
              <div className="sp-form-row">
                <label>Pricing model label</label>
                <input
                  type="text"
                  value={form.pricingModelLabel}
                  onChange={(e) => updateField('pricingModelLabel', e.target.value)}
                />
              </div>
            </div>
            <div className="sp-toggle-row">
              <label className="sp-toggle">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => updateField('isActive', e.target.checked)}
                />
                Active
              </label>
            </div>
          </div>

          <div className="sp-actions">
            <button type="submit" className="sp-btn sp-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create tier' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
